import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import AlertModal from '../components/AlertModal';
import LanguageToggle from '../components/LanguageToggle';
import { useAuth } from '../auth/AuthContext';
import { useLang } from '../LangContext';
import './LoginPage.css';

/* Atunears marka logosu — ekteki tasarımdan birebir SVG olarak yeniden oluşturuldu */
function AtunearsLogo({ size = 48 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Atunears"
      role="img"
    >
      {/* Cyan dairesel arka plan */}
      <circle cx="50" cy="50" r="50" fill="#28D2E6" />
      {/* Dış ses dalgası kavisi (en geniş) */}
      <path
        d="M 50 14 A 36 36 0 0 0 50 86"
        fill="none"
        stroke="#050a10"
        strokeWidth="8"
        strokeLinecap="round"
      />
      {/* Orta ses dalgası */}
      <path
        d="M 50 26 A 24 24 0 0 0 50 74"
        fill="none"
        stroke="#050a10"
        strokeWidth="8"
        strokeLinecap="round"
      />
      {/* İç dalga ucu — merkez noktanın etrafı */}
      <path
        d="M 50 38 A 12 12 0 0 0 50 62"
        fill="none"
        stroke="#050a10"
        strokeWidth="7"
        strokeLinecap="round"
      />
      {/* Yatay iğne / kol */}
      <rect x="50" y="46" width="48" height="8" fill="#050a10" />
      {/* Merkez nokta (cyan) */}
      <circle cx="50" cy="50" r="3.4" fill="#28D2E6" />
    </svg>
  );
}

export default function LoginPage() {
  const { login } = useAuth();
  const { locale: t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(user.role === 'Admin' ? '/admin' : '/forms', { replace: true });
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message || 'Giriş yapılamadı.' });
    } finally {
      setLoading(false);
    }
  }

  const currentYear = new Date().getFullYear();

  return (
    <div className="atu-root">
      {/* Sol panel — marka tarafı */}
      <aside className="atu-brand">
        <div className="atu-logo">
          <span className="atu-logo-mark">
            <AtunearsLogo size={34} />
          </span>
          <span className="atu-logo-text">
            <strong>Atunears</strong>
            <span>Personalized Audio</span>
          </span>
        </div>

        <div className="atu-hero">
          <span className="atu-eyebrow">
            <span className="pulse" />
            {t.login.tagline}
          </span>
          <h1 dangerouslySetInnerHTML={{ __html: t.login.heroTitle }} />
          <p>{t.login.heroDesc}</p>

          <ul className="atu-features">
            <li><span className="dot" /> {t.login.feature1}</li>
            <li><span className="dot" /> {t.login.feature2}</li>
            <li><span className="dot" /> {t.login.feature3}</li>
          </ul>
        </div>

        {/* Dekoratif büyük logo (arka plan görseli) */}
        <div className="atu-hero-art" aria-hidden="true">
          <AtunearsLogo size={640} />
        </div>

        <div className="atu-footer-note">
          <span>© {currentYear} Atunears</span>
        </div>
      </aside>

      {/* Sağ panel — form tarafı */}
      <section className="atu-form-side">
        <div className="atu-lang-bar">
          <LanguageToggle expanded />
        </div>
        <div className="atu-card">
          <div className="atu-card-head">
            <span className="atu-logo-mark">
              <AtunearsLogo size={30} />
            </span>
            <div>
              <h2 className="atu-card-title">{t.login.welcome}</h2>
              <p className="atu-card-sub">{t.login.subtitle}</p>
            </div>
          </div>

          <AlertModal isOpen={Boolean(alert)} type={alert?.type || 'info'} message={alert?.message || ''} onClose={() => setAlert(null)} />

          <form onSubmit={submit} noValidate>
            <div className="atu-field">
              <label htmlFor="atu-email">{t.login.email}</label>
              <div className="atu-input-wrap">
                <input
                  id="atu-email"
                  type="email"
                  autoComplete="email"
                  placeholder={t.login.emailPlaceholder}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </div>
            </div>

            <div className="atu-field">
              <label htmlFor="atu-pass">{t.login.password}</label>
              <div className="atu-input-wrap">
                <input
                  id="atu-pass"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder={t.login.passwordPlaceholder}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
                <button
                  type="button"
                  className="atu-toggle-eye"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? t.login.hidePassword : t.login.showPassword}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="atu-row">
              <label className="atu-check">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(event) => setRemember(event.target.checked)}
                />
                <span>{t.login.rememberMe}</span>
              </label>
              <a
                className="atu-link"
                href="#sifre-yardim"
                onClick={(event) => event.preventDefault()}
              >
                {t.login.forgotPassword}
              </a>
            </div>

            <button className="atu-submit" type="submit" disabled={loading}>
              {loading ? t.login.submitting : t.login.submit}
              <ArrowRight size={18} />
            </button>
          </form>

          <div className="atu-divider">
            <a
              className="atu-link"
              href="https://atunears.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              atunears.com
            </a>
          </div>
          <p className="atu-help">
            {t.login.noAccount}{' '}
            <a
              className="atu-link"
              href="https://atunears.com/contact"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.login.contactUs}
            </a>
          </p>
        </div>

        <div className="atu-credit">
          Powered by
          <a href="https://softana.com.tr" target="_blank" rel="noopener noreferrer">
            Softana
          </a>
        </div>
      </section>
    </div>
  );
}